// Kanemori Twin Towers (Nishi-Shinjuku buildings off-a and off-b, either side of the Twin Tower forecourt).
// Their footprints stay 12x13 tiles of solid ground; the art is drawn as two skyscrapers about 3.6x taller than the old
// five-tile face. The lowest 13 tiles of each front face are painted into the 2x scene over the footprint (paintTwinBase);
// everything above the footprint (setback roofs, upper tiers, crowns, masts and the sky bridge) is one upright sprite
// (paintTwinUpper, placed by render.ts) that hangs over the street north of the towers and is drawn above people and cars
// there. Purely visual: no tiles, collision or world data change. hd px throughout (32 per tile), light from the top-left.
import type { HdBuilding } from '../../common/src/data';
import { hash2 } from '../../common/src/util';
import type { LightFn } from './hd-registry';
import { type G, K, HT, px, frame, alpha, dk, lt, mix, disc, speckle, text, textW, glyph } from './hd-kit';

/** tile geometry (world tiles): footprint rows TOP..BASE-1, west tower from WEST_X, east tower from EAST_X, both W wide */
export const TWIN = { top: 118, base: 131, westX: 83, eastX: 101, w: 12 } as const;
const IDS: Record<string, boolean> = { 'off-a': false, 'off-b': true }; // id -> east tower
const W = TWIN.w * HT;                                // 384
const SHAFT = (TWIN.base - TWIN.top) * HT;            // 416: the shaft's front face fills the footprint exactly
/** hd px from the base line to the tip of the masts */
export const TWIN_RISE = 904;
/** the upright sprite: hd px size; its bottom edge sits on the footprint's north edge (tile row TWIN.top) */
export const TWIN_UPPER = { w: (TWIN.eastX + TWIN.w - TWIN.westX) * HT, h: TWIN_RISE - SHAFT } as const;

interface Tier { x0: number; x1: number; d0: number; d1: number; zb: number; zt: number }
// x across the tower, d = depth back from the front wall, z = height. Screen y = base - d - z (three-quarter view).
const TIERS: Tier[] = [
  { x0: 0, x1: W, d0: 0, d1: 256, zb: 0, zt: SHAFT },           // shaft: 13 storeys of face over the footprint
  { x0: 20, x1: W - 20, d0: 20, d1: 236, zb: SHAFT, zt: 512 },  // first setback
  { x0: 52, x1: W - 52, d0: 44, d1: 212, zb: 512, zt: 576 },    // crown tier with the helipad
];
const BRIDGE = { d0: 120, d1: 176, zb: 332, zt: 372 };

const FL = 20, BAY = 16; // storey height and window bay
const STRUCT = dk(K.slate, 0.62), STRUCT_LO = dk(K.slate, 0.74);

function pane(g: G, x: number, y: number, w: number, h: number, fl: number, b: number, seed: number, tier: number) {
  const section = hash2(fl * 3 + seed, (b >> 2) * 7 + tier * 31);
  const on = section < 0.32 && hash2(b * 5 + seed * 2, fl * 13 + tier) < 0.86;
  if (on) {
    const warm = hash2(fl >> 1, (b >> 2) + seed * 3) < 0.62, c = warm ? K.amber : K.cyan;
    px(g, x, y, w, h, dk(c, 0.38));
    px(g, x, y, w, 2, lt(dk(c, 0.1), 0.25));            // ceiling light
    px(g, x, y + h - 4, w, 4, dk(c, 0.62));              // desks / sills
    const r = hash2(b * 7 + seed, fl * 3 + tier * 5), sx = x + 3 + (b * 5 + fl) % (w - 6);
    if (r < 0.3) { px(g, sx, y + h - 10, 3, 6, dk(c, 0.8)); px(g, sx, y + h - 12, 3, 2, dk(c, 0.72)); } // someone working late
    else if (r < 0.55) { px(g, x + 2, y + h - 8, 5, 3, lt(K.cyan, 0.35)); px(g, x + 2, y + h - 5, 5, 1, dk(c, 0.8)); } // monitor glow
    else if (r < 0.78) { const d = 3 + ((b + fl) % 3) * 2; px(g, x, y + 2, w, d, dk(c, 0.55)); for (let j = y + 3; j < y + 2 + d; j += 2) px(g, x, j, w, 1, dk(c, 0.68)); } // half-drawn blinds
    else px(g, x + w - 4, y + 4, 3, h - 8, dk(c, 0.66)); // a partition
    return;
  }
  // dark glass: three panel tones in seeded runs per storey (no two floors repeat), a sky ramp and sill shadow,
  // the city's glare streaking across a few panes and neighbouring neon reflected in others
  const tone = hash2((b + (fl * 5 + seed) % 7) >> 1, fl * 17 + seed);
  const glare = (((b * 16 - fl * 11 + seed * 37) % 260) + 260) % 260;
  const base = glare < 30 ? mix(K.glass, K.glassHi, 0.45) : tone < 0.33 ? K.glass : tone < 0.7 ? mix(K.glass, K.glassLo, 0.5) : mix(K.glass, K.glassHi, 0.2);
  px(g, x, y, w, h, base);
  px(g, x, y, w, 3, mix(base, K.glassHi, 0.35));
  px(g, x, y + h - 3, w, 3, mix(base, K.glassLo, 0.7));
  px(g, x, y + 3, 1, h - 6, mix(base, K.glassHi, 0.22));
  const n = hash2(fl * 11 + seed, b * 3);
  if (glare < 30) { const k = (glare >> 2) % w; px(g, x + k, y + 1, 2, h - 4, alpha(lt(K.cyan, 0.4), 0.45)); }
  else if (n < 0.05) { px(g, x + 1, y + 5, w - 2, 3, alpha(K.pink, 0.42)); px(g, x + 3, y + 6, w - 6, 1, alpha(lt(K.pink, 0.4), 0.55)); } // a neon sign reflected
  else if (n < 0.08) px(g, x + 2, y + 4, w - 4, 4, alpha(K.amber, 0.26));
}

/** vertical sign column: KANEMORI in katakana on tower A, a pink LED ad panel on tower B */
function signColumn(g: G, x: number, y: number, h: number, east: boolean) {
  const w = 28, c = east ? K.pink : K.cyan;
  px(g, x - 4, y - 4, w + 8, h + 8, alpha(c, 0.1));
  px(g, x, y, w, h, K.ink); frame(g, x, y, w, h, K.metalLo); px(g, x + 1, y + 1, 1, h - 2, K.metalHi);
  for (const by of [y - 3, y + h]) { px(g, x + 6, by, 3, 3, K.metal); px(g, x + w - 9, by, 3, 3, K.metal); } // brackets
  if (!east) {
    const word = ['カ', 'ネ', 'モ', 'リ'], step = Math.floor((h - 8) / word.length);
    word.forEach((ch, i) => { const gy = y + 6 + i * step + (step - 12) / 2 | 0; px(g, x + 3, gy - 2, w - 6, 16, alpha(c, 0.22)); glyph(g, ch, x + 7, gy + 1, dk(c, 0.5), 2); glyph(g, ch, x + 7, gy, lt(c, 0.35), 2); });
  } else {
    let cy = y + 4; // stacked LED panels: a product strip, a scrolling bar, the brand mark, chevrons
    for (let k = 0; cy + 10 < y + h - 4; k++) {
      const ph = 10 + (k % 3) * 6, col = [K.pink, K.amber, K.cyan][k % 3];
      px(g, x + 3, cy, w - 6, ph, dk(col, 0.6));
      for (let i = x + 4; i < x + w - 4; i += 3) if (hash2(i, cy + k) < 0.55) px(g, i, cy + 1 + ((i + k) % (ph - 2)), 2, 1, col);
      px(g, x + 3, cy, w - 6, 1, lt(col, 0.4));
      cy += ph + 3;
    }
  }
}

/** a window cleaners' gondola on its cables, partway down a face */
function gondola(g: G, x: number, top: number, y: number) {
  px(g, x + 3, top, 1, y - top, alpha(K.metalHi, 0.7)); px(g, x + 36, top, 1, y - top, alpha(K.metalHi, 0.7));
  px(g, x, y, 40, 3, K.metal); px(g, x, y, 40, 1, K.metalHi);
  px(g, x, y + 3, 40, 8, K.metalLo); frame(g, x, y + 3, 40, 8, K.ink);
  for (let i = x + 5; i < x + 36; i += 6) px(g, i, y + 4, 1, 6, K.ink);
  px(g, x + 12, y - 6, 3, 6, K.ink); px(g, x + 24, y - 7, 3, 7, K.ink); // the crew
  px(g, x + 12, y - 8, 3, 2, K.amber); px(g, x + 24, y - 9, 3, 2, K.amber); // hard hats
  px(g, x + 1, y + 10, 2, 2, K.red);
}

/** the curtain wall: storeys of glass bays between metal spandrels, pilasters every four bays, a mechanical floor
 *  every seventh storey of the shaft, neon corner fins in the tower's accent colour */
function curtain(g: G, x: number, y: number, w: number, h: number, tier: number, east: boolean, seed: number, lobby: number) {
  const accent = east ? K.pink : K.cyan;
  px(g, x, y, w, h, STRUCT);
  const top = y + 8, bottom = y + h - lobby;
  for (let fy = top, fl = 0; fy + FL <= bottom; fy += FL, fl++) {
    // spandrel: a dark band each floor, lit top edge, slab line and a seeded run of panel joints
    px(g, x, fy, w, 6, STRUCT_LO); px(g, x, fy, w, 1, K.metal); px(g, x, fy + 1, w, 1, K.metalLo); px(g, x, fy + 5, w, 1, K.ink);
    for (let i = x + 7 + ((fl * 7 + seed) % 5) * 3; i < x + w - 7; i += 13 + ((fl + i) % 3) * 6) px(g, i, fy + 2, 1, 3, K.ink);
    if (tier === 0 && fl % 7 === 5) { // plant floor: louvres, no glass
      px(g, x + 6, fy + 5, w - 12, FL - 5, STRUCT_LO);
      for (let j = fy + 6; j < fy + FL - 1; j += 2) px(g, x + 6, j, w - 12, 1, K.metalLo);
      for (let i = x + 6 + 62; i < x + w - 8; i += 64) px(g, i, fy + 5, 2, FL - 5, K.ink);
      continue;
    }
    for (let bx = x + 7, b = 0; bx + 13 <= x + w - 7; bx += BAY, b++) pane(g, bx, fy + 6, 13, FL - 7, fl, b, seed, tier);
  }
  // pilasters every four bays with a lit left edge, then the building's own corners
  for (let i = x + 7 + 4 * BAY - 3; i < x + w - 20; i += 4 * BAY) { px(g, i, top, 3, bottom - top, K.metalLo); px(g, i, top, 1, bottom - top, K.metal); }
  // chamfered corners: a lit bevel on the west edge, a shaded one on the east, each with its highlight line
  px(g, x, y, 2, h, K.ink); px(g, x + 2, y, 3, h, K.metalHi); px(g, x + 3, y, 1, h, lt(K.metalHi, 0.3)); px(g, x + 5, y, 1, h, K.metal);
  px(g, x + w - 2, y, 2, h, K.ink); px(g, x + w - 7, y, 5, h, STRUCT_LO); px(g, x + w - 7, y, 1, h, K.metalLo);
  if (tier === 0) {
    // low enough on the shaft to be seen from the southern sidewalk
    // on the forecourt side of each tower, clear of the HUD's corner panels
    signColumn(g, east ? x + 70 : x + w - 98, top + 10 * FL + 4, 5 * FL - 8, east);
    if (east) gondola(g, x + 196, y, top + 10 * FL + 13);
  }
  // neon fins up both corners, broken at the plant floors so the shaft reads as stacked
  for (const fx of [x + 7, x + w - 9]) {
    px(g, fx - 2, top, 6, bottom - top - 4, alpha(accent, 0.12));
    px(g, fx, top, 2, bottom - top - 4, accent);
    px(g, fx, top, 1, bottom - top - 4, lt(accent, 0.55));
    if (tier === 0) for (let fy = top + 5 * FL; fy < bottom; fy += 7 * FL) px(g, fx, fy, 2, FL, K.ink);
  }
  // cornice: metal cap and a lit accent line under it
  px(g, x, y, w, 6, K.metal); px(g, x, y, w, 1, K.metalHi); px(g, x, y + 6, w, 1, K.ink);
  px(g, x + 8, y + 7, w - 16, 1, alpha(accent, 0.8));
}

/** double-height lobby at street level: canopy, name plate, revolving door, warm hall behind the glass */
function lobby(g: G, x: number, y: number, w: number, h: number, east: boolean, L?: LightFn) {
  const accent = east ? K.pink : K.cyan;
  px(g, x + 2, y, w - 4, h, STRUCT_LO);
  px(g, x + 2, y, w - 4, 5, K.concreteHi); px(g, x + 2, y + 5, w - 4, 1, K.ink);
  // hall: warm interior with ceiling lights, columns, planters
  const gy = y + 18, gh = h - 22;
  px(g, x + 10, gy, w - 20, gh, dk(K.amber, 0.66));
  px(g, x + 10, gy, w - 20, 3, dk(K.amber, 0.2));
  for (let i = x + 18; i < x + w - 18; i += 12) px(g, i, gy + 1, 6, 1, lt(K.amber, 0.5));
  px(g, x + 10, gy + gh - 6, w - 20, 6, dk(K.amber, 0.78)); // floor
  for (let i = x + 10; i < x + w - 10; i += 32) { px(g, i, gy, 4, gh, K.ink); px(g, i + 1, gy, 1, gh, K.metalLo); } // mullions
  for (const i of [x + 26, x + w - 40]) { px(g, i, gy + gh - 16, 12, 10, K.leafLo); px(g, i + 2, gy + gh - 20, 8, 6, K.leaf); px(g, i + 3, gy + gh - 19, 3, 2, K.leafHi); px(g, i + 1, gy + gh - 6, 10, 5, K.metalLo); }
  // reception desk and a guard's silhouette
  const dx = x + (east ? 52 : w - 108);
  px(g, dx, gy + gh - 15, 56, 9, K.metalLo); px(g, dx, gy + gh - 15, 56, 1, K.metalHi); px(g, dx + 4, gy + gh - 14, 48, 1, alpha(accent, 0.7));
  px(g, dx + 30, gy + gh - 25, 5, 10, K.ink); px(g, dx + 31, gy + gh - 28, 3, 3, K.ink);
  // revolving door in the middle
  const cx = x + w / 2;
  px(g, cx - 22, gy - 2, 44, gh + 2, K.ink);
  px(g, cx - 20, gy, 40, gh, dk(K.cyan, 0.55));
  px(g, cx - 20, gy, 40, 3, lt(dk(K.cyan, 0.3), 0.3));
  px(g, cx - 1, gy, 2, gh, K.metalHi); px(g, cx - 18, gy + gh / 2, 36, 1, K.metalHi);
  px(g, cx - 14, gy + 3, 2, gh - 6, alpha(K.white, 0.25));
  // canopy with the name plate
  px(g, x + 18, y + 8, w - 36, 7, K.metal); px(g, x + 18, y + 8, w - 36, 1, K.metalHi); px(g, x + 18, y + 15, w - 36, 2, K.ink);
  px(g, x + 22, y + 17, w - 44, 1, alpha(accent, 0.9));
  const name = 'KANEMORI', tw = textW(name, 1, 2);
  px(g, cx - tw / 2 - 6, y + 6, tw + 12, 11, K.ink); frame(g, cx - tw / 2 - 6, y + 6, tw + 12, 11, dk(accent, 0.4));
  text(g, name, cx - tw / 2, y + 8, accent, 1, 2);
  const tag = east ? 'B' : 'A';
  px(g, x + 26, y + 6, 11, 11, K.ink); text(g, tag, x + 29, y + 8, K.warmHi);
  px(g, x + w - 37, y + 6, 11, 11, K.ink); text(g, tag, x + w - 34, y + 8, K.warmHi);
  // kick plate
  px(g, x, y + h - 4, w, 3, K.concreteLo); px(g, x, y + h - 1, w, 1, K.ink);
  if (L) {
    L(cx - 48, y + h, 96, 22, K.amber, 0.34, true);
    L(x + 22, y + 15, w - 44, 6, accent, 0.3, false);
  }
}

/** a setback roof: tar membrane, parapet, maintenance rails, aviation lights at the corners */
function roofDeck(g: G, x: number, y: number, w: number, h: number, tier: number, east: boolean, seed: number) {
  px(g, x, y, w, h, K.tar);
  speckle(g, x + 4, y + 4, w - 8, h - 8, K.tarHi, 0.05, seed + tier);
  speckle(g, x + 4, y + 4, w - 8, h - 8, K.tarLo, 0.05, seed + tier + 9);
  frame(g, x, y, w, h, K.ink, 2);
  px(g, x + 2, y + 2, w - 4, 3, K.concreteHi); px(g, x + 2, y + 2, 3, h - 4, K.concreteHi);
  px(g, x + w - 5, y + 5, 3, h - 7, K.concreteLo); px(g, x + 5, y + h - 4, w - 10, 2, K.concreteHi);
  // gondola track for the window cleaners, inside the parapet
  px(g, x + 8, y + 9, w - 16, 1, K.metalHi); px(g, x + 8, y + 12, w - 16, 1, K.metalLo);
  for (let i = x + 12; i < x + w - 12; i += 24) px(g, i, y + 9, 2, 4, K.metal);
  const accent = east ? K.pink : K.cyan;
  for (const [ax, ay] of [[x + 4, y + 4], [x + w - 8, y + 4], [x + 4, y + h - 8], [x + w - 8, y + h - 8]]) {
    px(g, ax - 3, ay - 3, 10, 10, alpha(K.pink, 0.16)); px(g, ax, ay, 4, 4, K.red); px(g, ax, ay, 2, 2, lt(K.pink, 0.5));
  }
  if (tier === 1) { // condenser bank along the front ring
    for (let i = x + 26; i + 30 < x + w - 26; i += 44) {
      px(g, i, y + h - 18, 30, 12, K.metalLo); frame(g, i, y + h - 18, 30, 12, K.ink);
      for (let k = 3; k < 28; k += 3) px(g, i + k, y + h - 16, 1, 8, K.ink);
    }
    px(g, x + 6, y + 15, w - 12, 1, alpha(accent, 0.25));
  }
}

/** crown roof: helipad, penthouse and the lattice mast */
function crown(g: G, x: number, y: number, w: number, h: number, east: boolean) {
  const accent = east ? K.pink : K.cyan;
  // helipad: a raised deck on the sunny half, the mast and penthouse on the other
  const cx = east ? x + w * 0.62 : x + w * 0.38, cy = y + h * 0.52 | 0, r = 58;
  for (let j = y + 18; j < y + h - 10; j += 16) px(g, x + 6, j, w - 12, 1, alpha(K.tarHi, 0.45)); // deck pavers
  for (let i = x + 22; i < x + w - 10; i += 32) px(g, i, y + 6, 1, h - 14, alpha(K.tarHi, 0.3));
  disc(g, cx + 4, cy + 5, r, r, alpha(K.night, 0.45));
  disc(g, cx, cy, r, r, K.ink);
  disc(g, cx, cy, r - 2, r - 2, dk(K.slate, 0.5));
  disc(g, cx, cy, r - 6, r - 6, K.amber);
  disc(g, cx, cy, r - 9, r - 9, dk(K.slate, 0.55));
  speckle(g, cx - 40, cy - 40, 80, 80, dk(K.slate, 0.62), 0.04, 5);
  // the H
  const H = K.paint;
  px(g, cx - 18, cy - 24, 8, 48, H); px(g, cx + 10, cy - 24, 8, 48, H); px(g, cx - 10, cy - 4, 20, 8, H);
  px(g, cx - 18, cy - 24, 8, 2, lt(H, 0.3)); px(g, cx + 10, cy - 24, 8, 2, lt(H, 0.3));
  for (let k = 0; k < 12; k++) { // edge lights round the pad
    const a = k / 12 * Math.PI * 2, lx = Math.round(cx + Math.cos(a) * (r - 1)), ly = Math.round(cy + Math.sin(a) * (r - 1));
    px(g, lx - 3, ly - 3, 7, 7, alpha(K.amber, 0.2)); px(g, lx - 1, ly - 1, 3, 3, K.warmHi);
  }
  // penthouse (lift motor room) at the back of the other half
  const pw = 74, ph = 52, ppx = east ? x + 16 : x + w - 16 - pw, ppy = y + 14;
  px(g, ppx + 4, ppy + 6, pw, ph, alpha(K.night, 0.4));
  px(g, ppx, ppy, pw, ph - 18, K.concrete); frame(g, ppx, ppy, pw, ph - 18, K.ink); px(g, ppx + 2, ppy + 2, pw - 4, 2, K.concreteHi);
  px(g, ppx, ppy + ph - 18, pw, 18, K.concreteLo); px(g, ppx, ppy + ph - 18, pw, 1, K.metalHi); // its south wall
  px(g, ppx + 8, ppy + ph - 14, 12, 14, K.ink); px(g, ppx + 9, ppy + ph - 13, 10, 2, dk(K.amber, 0.3)); // door
  for (let i = ppx + 28; i < ppx + pw - 8; i += 10) px(g, i, ppy + ph - 12, 6, 6, K.metalLo);
  for (let k = 0; k < 2; k++) { // cooling fans beside the penthouse
    const fx = ppx + 18 + k * 38, fy = ppy + ph + 20;
    disc(g, fx + 2, fy + 3, 14, 14, alpha(K.night, 0.4));
    disc(g, fx, fy, 14, 14, K.ink); disc(g, fx, fy, 12, 12, K.metalLo);
    px(g, fx - 11, fy, 23, 1, K.metalHi); px(g, fx, fy - 11, 1, 23, K.metalHi);
    for (let t = -7; t <= 7; t++) { px(g, fx + t, fy + t, 1, 1, K.metal); px(g, fx + t, fy - t, 1, 1, K.metal); }
    disc(g, fx, fy, 3, 3, K.ink); px(g, fx - 1, fy - 1, 2, 2, K.metalHi);
  }
  // the mast: guyed lattice rising from the penthouse roof, platforms, a beacon at the tip
  const mx = ppx + pw / 2 | 0, mb = ppy + 16, mh = 132;
  px(g, mx - 9, mb - 2, 18, 6, K.ink); px(g, mx - 8, mb - 2, 16, 2, K.metal);
  px(g, mx - 3, mb - mh, 1, mh, K.ink); px(g, mx + 3, mb - mh, 1, mh, K.ink);
  px(g, mx - 2, mb - mh, 5, mh, alpha(K.metal, 0.35));
  for (let j = mb - 4; j > mb - mh + 4; j -= 6) { px(g, mx - 2, j, 5, 1, K.metalHi); px(g, mx - 2 + ((j >> 1) & 3), j - 3, 1, 3, K.metalLo); }
  for (const j of [mb - 46, mb - 92]) { px(g, mx - 8, j, 17, 2, K.metalHi); px(g, mx - 8, j + 2, 17, 1, K.ink); px(g, mx - 7, j + 3, 2, 2, K.red); px(g, mx + 6, j + 3, 2, 2, K.red); }
  for (const [dx, j] of [[-30, mb + 18], [30, mb + 18]]) { // guy wires
    const n = 40; for (let k = 0; k < n; k++) px(g, Math.round(mx + dx * (1 - k / n)), Math.round(j - (j - (mb - 70)) * k / n), 1, 1, alpha(K.metalHi, 0.5));
  }
  const ty = mb - mh;
  px(g, mx - 10, ty - 10, 21, 21, alpha(K.pink, 0.1)); px(g, mx - 6, ty - 6, 13, 13, alpha(K.pink, 0.22));
  px(g, mx - 2, ty - 3, 5, 5, K.red); px(g, mx - 1, ty - 2, 2, 2, lt(K.pink, 0.6));
  // crown light: a band along the front edge of the top roof in the accent colour
  px(g, x + 6, y + h - 7, w - 12, 3, alpha(accent, 0.25)); px(g, x + 6, y + h - 6, w - 12, 1, accent);
}

/** both towers' complete art, for the canvas whose base line (tower front walls) is at `base`; ox = west tower left */
/** one tower. 'base' is the shaft's front face (it fills the footprint); 'upper' is everything above the footprint,
 *  painted back to front: shaft roof, then each setback's face, its shadow on the roof below, and its roof. */
function paintTower(g: G, ox: number, base: number, east: boolean, part: 'base' | 'upper', L?: LightFn) {
  const seed = east ? 71 : 23;
  TIERS.forEach((t, i) => {
    const fx = ox + t.x0, fw = t.x1 - t.x0;
    if (part === 'base') {
      if (i > 0) return;
      const fy = base - t.zt, lob = 62;
      curtain(g, fx, fy, fw, t.zt, 0, east, seed, lob);
      lobby(g, fx, base - lob, fw, lob, east, L);
      return;
    }
    if (i > 0) {
      curtain(g, fx, base - t.d0 - t.zt, fw, t.zt - t.zb, i, east, seed, 0);
      px(g, fx + fw, base - t.d1 - t.zb + 6, 8, t.d1 - t.d0 - 6, alpha(K.night, 0.35)); // its shadow on the roof below
    }
    const ry = base - t.d1 - t.zt, rh = t.d1 - t.d0;
    roofDeck(g, fx, ry, fw, rh, i, east, seed);
    if (i === TIERS.length - 1) crown(g, fx, ry, fw, rh, east);
  });
}

/** the glazed sky bridge across the forecourt, between the towers' tenth and eleventh storeys */
function skyBridge(g: G, x: number, w: number, base: number) {
  const fy = base - BRIDGE.d0 - BRIDGE.zt, fh = BRIDGE.zt - BRIDGE.zb, ry = base - BRIDGE.d1 - BRIDGE.zt, rh = BRIDGE.d1 - BRIDGE.d0;
  px(g, x, ry, w, rh, K.metalLo); frame(g, x, ry, w, rh, K.ink);
  for (let i = x + 6; i < x + w - 4; i += 12) px(g, i, ry + 2, 2, rh - 4, K.metal); // roof ribs
  px(g, x, ry + 1, w, 1, K.metalHi);
  px(g, x, fy, w, fh, K.ink);
  px(g, x, fy + 4, w, fh - 10, dk(K.cyan, 0.6)); px(g, x, fy + 4, w, 3, dk(K.cyan, 0.35));
  px(g, x, fy + 15, w, 4, alpha(K.amber, 0.6)); // walkway lights inside
  for (let i = x + 4, k = 0; i < x + w - 8; i += 16, k++) { // truss: a diagonal per bay
    for (let j = 0; j < fh - 10; j++) px(g, i + ((k & 1) ? j * 16 / (fh - 10) : 16 - j * 16 / (fh - 10)) | 0, fy + 4 + j, 1, 1, K.metalLo);
    px(g, i, fy + 4, 2, fh - 10, K.metal);
  }
  px(g, x + 52, fy + 11, 3, 8, K.ink); px(g, x + 120, fy + 11, 3, 8, K.ink); // two people crossing
  px(g, x, fy + fh - 6, w, 6, K.metalLo); px(g, x, fy + fh - 6, w, 1, K.metalHi);
  px(g, x, fy + fh - 1, w, 1, alpha(K.pink, 0.8));
  px(g, x, fy + fh, w, 6, alpha(K.night, 0.3));
}

/** Ground pass (2x scene, called from paintBuilding): the shaft's front face with the lobby over the whole footprint.
 *  Returns false for every other building. */
export function paintTwinBase(g: G, b: HdBuilding, x: number, y: number, L: LightFn): boolean {
  const east = IDS[b.id];
  if (east === undefined || b.y !== TWIN.top || b.x !== (east ? TWIN.eastX : TWIN.westX)) return false;
  g.save(); g.beginPath(); g.rect(x, y, b.w * HT, b.h * HT); g.clip();
  paintTower(g, x, y + b.h * HT, east, 'base', L);
  g.restore();
  // everything above the footprint is the upright sprite; this keeps the lowest part of the facade in the ground
  return true;
}

/** Upright pass: paints the setbacks, crowns, masts and sky bridge onto a TWIN_UPPER-sized canvas whose bottom edge is the
 *  footprints' north edge and whose left edge is the west tower's west wall. */
export function paintTwinUpper(g: G) {
  const base = TWIN_UPPER.h + SHAFT;
  const ex = (TWIN.eastX - TWIN.westX) * HT;
  skyBridge(g, W, ex - W, base);
  paintTower(g, 0, base, false, 'upper');
  paintTower(g, ex, base, true, 'upper');
}

/** World-px rectangles (x0, y0, x1, y1) where the upright art hides what is behind it, for the see-through fade. */
export function twinOcclusion(tile: number): [number, number, number, number][] {
  const s = tile / HT, yTop = TWIN.top * tile, rise = (TWIN_RISE - SHAFT - 120) * s; // the masts are thin: ignore them
  return [
    [TWIN.westX * tile, yTop - rise, (TWIN.westX + TWIN.w) * tile, yTop + 4],
    [TWIN.eastX * tile, yTop - rise, (TWIN.eastX + TWIN.w) * tile, yTop + 4],
    [(TWIN.westX + TWIN.w) * tile, TWIN.base * tile - (BRIDGE.d1 + BRIDGE.zt) * s - 4, TWIN.eastX * tile, TWIN.base * tile - (BRIDGE.d0 + BRIDGE.zb) * s + 2],
  ];
}
